import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { TabView, TabPanel } from 'primereact/tabview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputMask } from 'primereact/inputmask';
import { Password } from 'primereact/password';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { Image } from 'primereact/image';
import { Checkbox } from 'primereact/checkbox';
import { InputSwitch } from 'primereact/inputswitch';
import { Chips } from 'primereact/chips';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Panel } from 'primereact/panel';
import { Tooltip } from 'primereact/tooltip';
import { Badge } from 'primereact/badge';
import { Avatar } from 'primereact/avatar';
import { ProgressBar } from 'primereact/progressbar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { classNames } from 'primereact/utils';
import authService from '../services/auth.service';
import axiosInstance from '../config/axios.config';
// import './Settings.css';

const Settings = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const fileUploadRef = useRef(null);
    
    // Loading and Profile Completion State
    const [loading, setLoading] = useState(true);
    const [profileCompletion, setProfileCompletion] = useState(null);
    const [showProfileAlert, setShowProfileAlert] = useState(false);
    
    // Profile Settings State
    const [profileData, setProfileData] = useState({
        avatar: '',
        name: '',
        email: '',
        emailVerified: false,
        company: '',
        phone: '',
        address: '',
        bio: '',
        first_name: '',
        last_name: '',
        mobile_number: '',
        city: '',
        pincode: '',
        state: ''
    });
    
    // API Configuration State
    const [apiConfig, setApiConfig] = useState({
        apiKey: 'pk_live_1234567890abcdef',
        webhookUrl: 'https://api.example.com/webhook',
        ipWhitelist: ['192.168.1.1', '10.0.0.1'],
        testMode: false
    });
    
    // Security Settings State
    const [securitySettings, setSecuritySettings] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
        twoFactorEnabled: false,
        passwordStrength: 0
    });
    
    // Notification Preferences State
    const [notifications, setNotifications] = useState({
        emailNotifications: {
            reportGenerated: true,
            creditLow: true,
            monthlyDigest: true,
            productUpdates: false,
            security: true
        },
        smsEnabled: false,
        frequency: 'immediate',
        reportDelivery: 'email'
    });
    
    // Billing Information State.
    //
    // Seeded empty, not with a specimen company (GH #198). This used to open on
    // "Acme Corporation", GST 29ABCDE1234F1Z5, PAN ABCDE1234F and a Mumbai
    // address — presented in an editable form as the TENANT'S OWN billing
    // details. `setBillingInfo` is only ever called from the onChange handlers,
    // so nothing replaced them: whatever the admin failed to notice and correct
    // was what the field said. Blank fields prompt; plausible wrong ones do not.
    const [billingInfo, setBillingInfo] = useState({
        companyName: '',
        gstNumber: '',
        panNumber: '',
        billingAddress: '',
        billingEmail: ''
    });

    // Login history. There is no endpoint for it yet, so the list is empty and
    // the table says so. It previously showed four invented sessions with
    // invented IPs, devices and cities — including a "failed" login from
    // Bangalore. Fabricated security history is the kind a user acts on.
    const [loginHistory] = useState([]);
    
    // Active Sessions Sample Data
    const [activeSessions] = useState([
        { id: 1, device: 'Chrome on Windows', ip: '192.168.1.100', location: 'Mumbai, India', lastActive: new Date(), current: true },
        { id: 2, device: 'Mobile App iOS', ip: '192.168.1.103', location: 'Mumbai, India', lastActive: new Date('2024-01-20 09:00') },
        { id: 3, device: 'Firefox on MacOS', ip: '192.168.1.101', location: 'Delhi, India', lastActive: new Date('2024-01-19 18:30') }
    ]);
    
    // Invoice History Sample Data
    const [invoices] = useState([
        { id: 'INV-2024-001', date: new Date('2024-01-15'), amount: 5000, credits: 500, status: 'paid', downloadUrl: '/api/invoices/INV-2024-001.pdf' },
        { id: 'INV-2023-012', date: new Date('2023-12-15'), amount: 10000, credits: 1000, status: 'paid', downloadUrl: '/api/invoices/INV-2023-012.pdf' },
        { id: 'INV-2023-011', date: new Date('2023-11-15'), amount: 5000, credits: 500, status: 'paid', downloadUrl: '/api/invoices/INV-2023-011.pdf' }
    ]);
    
    // Fetch user data and profile completion status
    useEffect(() => {
        fetchUserData();
        fetchProfileCompletion();
    }, []);
    
    const fetchUserData = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/auth/user');
            if (response.data.success) {
                const userData = response.data.user;
                setProfileData({
                    avatar: userData.avatar || '',
                    name: userData.name || '',
                    email: userData.email || '',
                    emailVerified: userData.email_verified_at ? true : false,
                    company: userData.company_name || '',
                    phone: userData.mobile_number || '',
                    address: userData.address || '',
                    bio: userData.bio || '',
                    first_name: userData.first_name || '',
                    last_name: userData.last_name || '',
                    mobile_number: userData.mobile_number || '',
                    city: userData.city || '',
                    pincode: userData.pincode || '',
                    state: userData.state || ''
                });
                
                // Check if profile completion is included
                if (userData.profile_completion) {
                    setProfileCompletion(userData.profile_completion);
                    setShowProfileAlert(!userData.profile_completion.completed);
                }
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
            toast.current?.show({
                severity: 'warn',
                summary: 'Data Load Error',
                detail: 'Using cached data. Please check your connection.',
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };
    
    const fetchProfileCompletion = async () => {
        try {
            const response = await axiosInstance.get('/user-details/status');
            if (response.data.success) {
                setProfileCompletion(response.data.data.status);
                setShowProfileAlert(!response.data.data.status.step1_completed);
            }
        } catch (error) {
            console.error('Error fetching profile completion:', error);
        }
    };
    
    const handleCompleteProfile = () => {
        navigate('/app/profile/complete');
    };
    
    // Notification frequency options
    const frequencyOptions = [
        { label: 'Immediate', value: 'immediate' },
        { label: 'Daily Digest', value: 'daily' },
        { label: 'Weekly Summary', value: 'weekly' },
        { label: 'Monthly Report', value: 'monthly' }
    ];
    
    const deliveryOptions = [
        { label: 'Email Only', value: 'email' },
        { label: 'In-App Only', value: 'app' },
        { label: 'Both Email & In-App', value: 'both' }
    ];
    
    // Avatar upload handler
    const onAvatarUpload = (event) => {
        const file = event.files[0];
        const reader = new FileReader();
        
        reader.onloadend = (e) => {
            setProfileData({ ...profileData, avatar: e.target.result });
            toast.current.show({
                severity: 'success',
                summary: 'Avatar Updated',
                detail: 'Your profile picture has been updated',
                life: 3000
            });
        };
        
        reader.readAsDataURL(file);
        fileUploadRef.current.clear();
    };
    
    // Save profile
    const saveProfile = () => {
        toast.current.show({
            severity: 'success',
            summary: 'Profile Updated',
            detail: 'Your profile information has been saved',
            life: 3000
        });
    };
    
    // Copy API key
    const copyApiKey = () => {
        navigator.clipboard.writeText(apiConfig.apiKey);
        toast.current.show({
            severity: 'success',
            summary: 'Copied',
            detail: 'API key copied to clipboard',
            life: 2000
        });
    };
    
    // Regenerate API key
    const regenerateApiKey = () => {
        confirmDialog({
            message: 'Are you sure you want to regenerate your API key? The current key will be invalidated.',
            header: 'Regenerate API Key',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => {
                const newKey = 'pk_live_' + Math.random().toString(36).substring(2, 18);
                setApiConfig({ ...apiConfig, apiKey: newKey });
                toast.current.show({
                    severity: 'success',
                    summary: 'API Key Regenerated',
                    detail: 'Your new API key has been generated',
                    life: 3000
                });
            }
        });
    };
    
    // Calculate password strength
    const calculatePasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength += 25;
        if (password.length >= 12) strength += 25;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 25;
        if (/[0-9]/.test(password) && /[^a-zA-Z0-9]/.test(password)) strength += 25;
        return strength;
    };
    
    // Handle password change
    const handlePasswordChange = (value) => {
        setSecuritySettings({
            ...securitySettings,
            newPassword: value,
            passwordStrength: calculatePasswordStrength(value)
        });
    };
    
    // Change password
    const changePassword = async () => {
        if (securitySettings.newPassword !== securitySettings.confirmPassword) {
            toast.current.show({
                severity: 'error',
                summary: 'Password Mismatch',
                detail: 'New password and confirmation do not match',
                life: 3000
            });
            return;
        }
        
        try {
            const response = await authService.changePassword({
                current_password: securitySettings.currentPassword,
                password: securitySettings.newPassword,
                password_confirmation: securitySettings.confirmPassword
            });
            
            if (response.success) {
                toast.current.show({
                    severity: 'success',
                    summary: 'Password Changed',
                    detail: 'Your password has been updated successfully',
                    life: 3000
                });
                
                setSecuritySettings({
                    ...securitySettings,
                    currentPassword: '',
                    newPassword: '',
                    confirmPassword: '',
                    passwordStrength: 0
                });
            }
        } catch (error) {
            const errorMessage = error.response?.data?.errors?.current_password?.[0] || 
                               error.response?.data?.message || 
                               'Failed to change password';
            
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMessage,
                life: 3000
            });
        }
    };
    
    // Enable 2FA
    const toggle2FA = (value) => {
        if (value) {
            confirmDialog({
                message: 'Enable two-factor authentication for enhanced security?',
                header: 'Enable 2FA',
                icon: 'pi pi-shield',
                accept: () => {
                    setSecuritySettings({ ...securitySettings, twoFactorEnabled: true });
                    toast.current.show({
                        severity: 'success',
                        summary: '2FA Enabled',
                        detail: 'Two-factor authentication has been enabled',
                        life: 3000
                    });
                }
            });
        } else {
            setSecuritySettings({ ...securitySettings, twoFactorEnabled: false });
        }
    };
    
    // Revoke session
    const revokeSession = (session) => {
        confirmDialog({
            message: `Revoke session from ${session.device}?`,
            header: 'Revoke Session',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: () => {
                toast.current.show({
                    severity: 'success',
                    summary: 'Session Revoked',
                    detail: 'The session has been terminated',
                    life: 3000
                });
            }
        });
    };
    
    // Save notification preferences
    const saveNotifications = () => {
        toast.current.show({
            severity: 'success',
            summary: 'Preferences Saved',
            detail: 'Your notification preferences have been updated',
            life: 3000
        });
    };
    
    // Save billing information
    const saveBillingInfo = () => {
        toast.current.show({
            severity: 'success',
            summary: 'Billing Info Updated',
            detail: 'Your billing information has been saved',
            life: 3000
        });
    };
    
    // Column templates
    const dateTemplate = (rowData) => {
        return new Intl.DateTimeFormat('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short'
        }).format(rowData.date);
    };
    
    const statusTemplate = (rowData) => {
        return (
            <Tag 
                value={rowData.status.toUpperCase()} 
                severity={rowData.status === 'success' ? 'success' : 'danger'}
            />
        );
    };
    
    const sessionActionTemplate = (rowData) => {
        if (rowData.current) {
            return <Badge value="Current" severity="success" />;
        }
        return (
            <Button
                icon="pi pi-times"
                aria-label="Revoke Session"
                rounded
                text
                severity="danger"
                onClick={() => revokeSession(rowData)}
                tooltip="Revoke Session"
                tooltipOptions={{ position: 'left' }}
            />
        );
    };
    
    const invoiceActionTemplate = (rowData) => {
        return (
            <Button
                icon="pi pi-download"
                aria-label="Download Invoice"
                rounded
                text
                severity="primary"
                onClick={() => {
                    toast.current.show({
                        severity: 'info',
                        summary: 'Downloading',
                        detail: `Downloading invoice ${rowData.id}`,
                        life: 3000
                    });
                }}
                tooltip="Download Invoice"
                tooltipOptions={{ position: 'left' }}
            />
        );
    };
    
    // Get password strength color
    const getPasswordStrengthColor = () => {
        const strength = securitySettings.passwordStrength;
        if (strength <= 25) return '#ef4444';
        if (strength <= 50) return '#f59e0b';
        if (strength <= 75) return '#3b82f6';
        return '#10b981';
    };
    
    const getPasswordStrengthLabel = () => {
        const strength = securitySettings.passwordStrength;
        if (strength <= 25) return 'Weak';
        if (strength <= 50) return 'Fair';
        if (strength <= 75) return 'Good';
        return 'Strong';
    };
    
    if (loading) {
        return (
            <div className="flex justify-content-center align-items-center" style={{ height: '100vh' }}>
                <ProgressSpinner />
            </div>
        );
    }
    
    return (
        <div className="settings-page p-4">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".custom-tooltip" />
            
            {/* Profile Completion Alert */}
            {showProfileAlert && profileCompletion && (
                <Message 
                    severity="warn" 
                    className="mb-3 w-full"
                    content={
                        <div className="flex align-items-center justify-content-between w-full">
                            <div className="flex align-items-center gap-3">
                                <i className="pi pi-exclamation-triangle text-2xl"></i>
                                <div>
                                    <div className="font-bold mb-1">Complete Your Profile</div>
                                    <div className="text-sm">
                                        Your profile is {profileCompletion.completion_percentage || 0}% complete. 
                                        Please complete your profile to access all features.
                                    </div>
                                    <ProgressBar 
                                        value={profileCompletion.completion_percentage || 0} 
                                        className="mt-2"
                                        style={{ height: '8px' }}
                                     aria-label="Progress" />
                                </div>
                            </div>
                            <Button 
                                label="Complete Profile" 
                                icon="pi pi-arrow-right"
                                onClick={handleCompleteProfile}
                                className="p-button-warning"
                            />
                        </div>
                    }
                />
            )}
            
            <h1 className="sr-only">Settings</h1>
            <Card title="Settings" className="settings-card">
                <TabView>
                    {/* Profile Settings */}
                    <TabPanel header="Profile" leftIcon="pi pi-user">
                        <div className="profile-settings">
                            <Panel header="Personal Information">
                                <div className="grid">
                                    <div className="col-12 md:col-3 text-center">
                                        <div className="avatar-section">
                                            {profileData.avatar ? (
                                                <Image
                                                    src={profileData.avatar}
                                                    alt="Profile"
                                                    width="150"
                                                    height="150"
                                                    className="avatar-image"
                                                    preview
                                                />
                                            ) : (
                                                <Avatar
                                                    label={profileData.name?.charAt(0)?.toUpperCase() || 'U'}
                                                    size="xlarge"
                                                    shape="circle"
                                                    style={{ width: '150px', height: '150px', fontSize: '3rem', backgroundColor: '#667eea', color: '#fff' }}
                                                />
                                            )}
                                            <FileUpload
                                                ref={fileUploadRef}
                                                mode="basic"
                                                accept="image/*"
                                                maxFileSize={1000000}
                                                onUpload={onAvatarUpload}
                                                auto
                                                chooseLabel="Change Avatar"
                                                className="mt-3"
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="col-12 md:col-9">
                                        <div className="grid p-fluid">
                                            <div className="col-12 md:col-6">
                                                <div className="field">
                                                    <label htmlFor="name">Full Name</label>
                                                    <InputText
                                                        id="name"
                                                        value={profileData.name}
                                                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12 md:col-6">
                                                <div className="field">
                                                    <label htmlFor="email">Email Address</label>
                                                    <div className="p-inputgroup">
                                                        <InputText
                                                            id="email"
                                                            value={profileData.email}
                                                            onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                                                        />
                                                        <span className="p-inputgroup-addon">
                                                            {profileData.emailVerified ? (
                                                                <i className="pi pi-verified text-green-500"></i>
                                                            ) : (
                                                                <Button label="Verify" className="p-button-sm" />
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            <div className="col-12 md:col-6">
                                                <div className="field">
                                                    <label htmlFor="company">Company</label>
                                                    <InputText
                                                        id="company"
                                                        value={profileData.company}
                                                        onChange={(e) => setProfileData({ ...profileData, company: e.target.value })}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12 md:col-6">
                                                <div className="field">
                                                    <label htmlFor="phone">Phone Number</label>
                                                    <InputMask
                                                        id="phone"
                                                        value={profileData.phone}
                                                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                                                        mask="+99 99999 99999"
                                                        placeholder="+91 98765 43210"
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12">
                                                <div className="field">
                                                    <label htmlFor="address">Address</label>
                                                    <InputTextarea
                                                        id="address"
                                                        value={profileData.address}
                                                        onChange={(e) => setProfileData({ ...profileData, address: e.target.value })}
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>
                                            
                                            <div className="col-12">
                                                <div className="field">
                                                    <label htmlFor="bio">Bio</label>
                                                    <InputTextarea
                                                        id="bio"
                                                        value={profileData.bio}
                                                        onChange={(e) => setProfileData({ ...profileData, bio: e.target.value })}
                                                        rows={3}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex justify-content-end gap-2 mt-3">
                                            <Button label="Cancel" severity="secondary" outlined />
                                            <Button label="Save Changes" icon="pi pi-check" onClick={saveProfile} />
                                        </div>
                                    </div>
                                </div>
                            </Panel>
                        </div>
                    </TabPanel>
                    
                    {/* API Configuration */}
                    <TabPanel header="API Configuration" leftIcon="pi pi-code">
                        <div className="api-settings">
                            <Panel header="API Key Management">
                                <div className="field">
                                    <label htmlFor="apiKey">API Key</label>
                                    <div className="p-inputgroup">
                                        <InputText
                                            id="apiKey"
                                            value={apiConfig.apiKey}
                                            readOnly
                                            className="font-mono"
                                        />
                                        <Button
                                            icon="pi pi-copy"
                                            aria-label="Copy API key to clipboard"
                                            className="custom-tooltip"
                                            onClick={copyApiKey}
                                            data-pr-tooltip="Copy to clipboard"
                                        />
                                        <Button
                                            icon="pi pi-refresh"
                                            aria-label="Regenerate API key"
                                            severity="warning"
                                            onClick={regenerateApiKey}
                                            className="custom-tooltip"
                                            data-pr-tooltip="Regenerate API key"
                                        />
                                    </div>
                                    <small className="text-gray-600">Keep your API key secure and never share it publicly</small>
                                </div>
                                
                                <div className="field mt-4">
                                    <label htmlFor="webhookUrl">Webhook URL</label>
                                    <InputText
                                        id="webhookUrl"
                                        value={apiConfig.webhookUrl}
                                        onChange={(e) => setApiConfig({ ...apiConfig, webhookUrl: e.target.value })}
                                        placeholder="https://your-domain.com/webhook"
                                        className="w-full"
                                    />
                                </div>
                                
                                <div className="field mt-4">
                                    <label htmlFor="ipWhitelist">IP Whitelist</label>
                                    <Chips
                                        id="ipWhitelist"
                                        value={apiConfig.ipWhitelist}
                                        onChange={(e) => setApiConfig({ ...apiConfig, ipWhitelist: e.value })}
                                        placeholder="Add IP address"
                                        className="w-full"
                                    />
                                    <small className="text-gray-600">Add IP addresses that are allowed to use your API key</small>
                                </div>
                                
                                <div className="field-checkbox mt-4">
                                    <Checkbox
                                        inputId="testMode"
                                        checked={apiConfig.testMode}
                                        onChange={(e) => setApiConfig({ ...apiConfig, testMode: e.checked })}
                                    />
                                    <label htmlFor="testMode" className="ml-2">Enable Test Mode</label>
                                </div>
                                
                                <Message 
                                    severity="info" 
                                    text="Test mode allows you to make API calls without consuming credits"
                                    className="mt-3"
                                />
                            </Panel>
                        </div>
                    </TabPanel>
                    
                    {/* Security Settings */}
                    <TabPanel header="Security" leftIcon="pi pi-shield">
                        <Accordion multiple activeIndex={[0]}>
                            <AccordionTab header="Change Password">
                                <div className="grid p-fluid">
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="currentPassword">Current Password</label>
                                            <Password
                                                id="currentPassword"
                                                value={securitySettings.currentPassword}
                                                onChange={(e) => setSecuritySettings({ ...securitySettings, currentPassword: e.target.value })}
                                                toggleMask
                                                feedback={false}
                                            />
                                        </div>
                                    </div>
                                    
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="newPassword">New Password</label>
                                            <Password
                                                id="newPassword"
                                                value={securitySettings.newPassword}
                                                onChange={(e) => handlePasswordChange(e.target.value)}
                                                toggleMask
                                                feedback={false}
                                            />
                                            {securitySettings.newPassword && (
                                                <div className="mt-2">
                                                    <div className="flex justify-content-between align-items-center mb-1">
                                                        <small>Password Strength</small>
                                                        <small>{getPasswordStrengthLabel()}</small>
                                                    </div>
                                                    <ProgressBar 
                                                        value={securitySettings.passwordStrength} 
                                                        showValue={false}
                                                        style={{ height: '8px', backgroundColor: getPasswordStrengthColor() }}
                                                     aria-label="Progress" />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    
                                    <div className="col-12">
                                        <div className="field">
                                            <label htmlFor="confirmPassword">Confirm New Password</label>
                                            <Password
                                                id="confirmPassword"
                                                value={securitySettings.confirmPassword}
                                                onChange={(e) => setSecuritySettings({ ...securitySettings, confirmPassword: e.target.value })}
                                                toggleMask
                                                feedback={false}
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="flex justify-content-end mt-3">
                                    <Button 
                                        label="Change Password" 
                                        icon="pi pi-check" 
                                        onClick={changePassword}
                                        disabled={!securitySettings.currentPassword || !securitySettings.newPassword || !securitySettings.confirmPassword}
                                    />
                                </div>
                            </AccordionTab>
                            
                            <AccordionTab header="Two-Factor Authentication">
                                <div className="two-factor-section">
                                    <div className="flex justify-content-between align-items-center mb-3">
                                        <div>
                                            <h4 className="m-0">Two-Factor Authentication</h4>
                                            <p className="text-gray-600 mt-1">Add an extra layer of security to your account</p>
                                        </div>
                                        <InputSwitch
                                            checked={securitySettings.twoFactorEnabled}
                                            onChange={(e) => toggle2FA(e.value)}
                                        />
                                    </div>
                                    
                                    {securitySettings.twoFactorEnabled && (
                                        <Message 
                                            severity="success" 
                                            text="Two-factor authentication is enabled. You'll need to enter a code from your authenticator app when logging in."
                                        />
                                    )}
                                </div>
                            </AccordionTab>
                            
                            <AccordionTab header="Login History">
                                <DataTable
                                    value={loginHistory}
                                    paginator
                                    rows={5}
                                    className="login-history-table"
                                    emptyMessage="Login history is not available yet."
                                >
                                    <Column field="date" header="Date & Time" body={dateTemplate} />
                                    <Column field="ip" header="IP Address" />
                                    <Column field="device" header="Device" />
                                    <Column field="location" header="Location" />
                                    <Column field="status" header="Status" body={statusTemplate} />
                                </DataTable>
                            </AccordionTab>
                            
                            <AccordionTab header="Active Sessions">
                                <DataTable value={activeSessions} className="sessions-table">
                                    <Column field="device" header="Device" />
                                    <Column field="ip" header="IP Address" />
                                    <Column field="location" header="Location" />
                                    <Column 
                                        field="lastActive" 
                                        header="Last Active" 
                                        body={(rowData) => new Date(rowData.lastActive).toLocaleString('en-IN')}
                                    />
                                    <Column header="Action" body={sessionActionTemplate} style={{ width: '100px' }} />
                                </DataTable>
                            </AccordionTab>
                        </Accordion>
                    </TabPanel>
                    
                    {/* Notification Preferences */}
                    <TabPanel header="Notifications" leftIcon="pi pi-bell">
                        <Panel header="Email Notifications">
                            <div className="notification-settings">
                                <div className="field-checkbox mb-3">
                                    <Checkbox
                                        inputId="reportGenerated"
                                        checked={notifications.emailNotifications.reportGenerated}
                                        onChange={(e) => setNotifications({
                                            ...notifications,
                                            emailNotifications: {
                                                ...notifications.emailNotifications,
                                                reportGenerated: e.checked
                                            }
                                        })}
                                    />
                                    <label htmlFor="reportGenerated" className="ml-2">Report Generated</label>
                                </div>
                                
                                <div className="field-checkbox mb-3">
                                    <Checkbox
                                        inputId="creditLow"
                                        checked={notifications.emailNotifications.creditLow}
                                        onChange={(e) => setNotifications({
                                            ...notifications,
                                            emailNotifications: {
                                                ...notifications.emailNotifications,
                                                creditLow: e.checked
                                            }
                                        })}
                                    />
                                    <label htmlFor="creditLow" className="ml-2">Low Credit Balance Alert</label>
                                </div>
                                
                                <div className="field-checkbox mb-3">
                                    <Checkbox
                                        inputId="monthlyDigest"
                                        checked={notifications.emailNotifications.monthlyDigest}
                                        onChange={(e) => setNotifications({
                                            ...notifications,
                                            emailNotifications: {
                                                ...notifications.emailNotifications,
                                                monthlyDigest: e.checked
                                            }
                                        })}
                                    />
                                    <label htmlFor="monthlyDigest" className="ml-2">Monthly Usage Digest</label>
                                </div>
                                
                                <div className="field-checkbox mb-3">
                                    <Checkbox
                                        inputId="productUpdates"
                                        checked={notifications.emailNotifications.productUpdates}
                                        onChange={(e) => setNotifications({
                                            ...notifications,
                                            emailNotifications: {
                                                ...notifications.emailNotifications,
                                                productUpdates: e.checked
                                            }
                                        })}
                                    />
                                    <label htmlFor="productUpdates" className="ml-2">Product Updates & New Features</label>
                                </div>
                                
                                <div className="field-checkbox mb-3">
                                    <Checkbox
                                        inputId="security"
                                        checked={notifications.emailNotifications.security}
                                        onChange={(e) => setNotifications({
                                            ...notifications,
                                            emailNotifications: {
                                                ...notifications.emailNotifications,
                                                security: e.checked
                                            }
                                        })}
                                    />
                                    <label htmlFor="security" className="ml-2">Security Alerts</label>
                                </div>
                            </div>
                        </Panel>
                        
                        <Panel header="Notification Settings" className="mt-4">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label className="block mb-2">SMS Notifications</label>
                                        <div className="flex align-items-center">
                                            <InputSwitch
                                                checked={notifications.smsEnabled}
                                                onChange={(e) => setNotifications({ ...notifications, smsEnabled: e.value })}
                                            />
                                            <span className="ml-2">{notifications.smsEnabled ? 'Enabled' : 'Disabled'}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="frequency">Notification Frequency</label>
                                        <Dropdown
                                            id="frequency"
                                            value={notifications.frequency}
                                            options={frequencyOptions}
                                            onChange={(e) => setNotifications({ ...notifications, frequency: e.value })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12">
                                    <div className="field">
                                        <label htmlFor="reportDelivery">Report Delivery Preference</label>
                                        <Dropdown
                                            id="reportDelivery"
                                            value={notifications.reportDelivery}
                                            options={deliveryOptions}
                                            onChange={(e) => setNotifications({ ...notifications, reportDelivery: e.value })}
                                            className="w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-content-end mt-3">
                                <Button label="Save Preferences" icon="pi pi-check" onClick={saveNotifications} />
                            </div>
                        </Panel>
                    </TabPanel>
                    
                    {/* Billing & Invoices */}
                    <TabPanel header="Billing" leftIcon="pi pi-wallet">
                        <Panel header="Billing Information">
                            <div className="grid p-fluid">
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="companyName">Company Name</label>
                                        <InputText
                                            id="companyName"
                                            value={billingInfo.companyName}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, companyName: e.target.value })}
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="gstNumber">GST Number</label>
                                        <InputMask
                                            id="gstNumber"
                                            value={billingInfo.gstNumber}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, gstNumber: e.target.value })}
                                            mask="99aaaaa9999a9a9"
                                            placeholder="29ABCDE1234F1Z5"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="panNumber">PAN Number</label>
                                        <InputMask
                                            id="panNumber"
                                            value={billingInfo.panNumber}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, panNumber: e.target.value })}
                                            mask="aaaaa9999a"
                                            placeholder="ABCDE1234F"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12 md:col-6">
                                    <div className="field">
                                        <label htmlFor="billingEmail">Billing Email</label>
                                        <InputText
                                            id="billingEmail"
                                            value={billingInfo.billingEmail}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, billingEmail: e.target.value })}
                                            type="email"
                                        />
                                    </div>
                                </div>
                                
                                <div className="col-12">
                                    <div className="field">
                                        <label htmlFor="billingAddress">Billing Address</label>
                                        <InputTextarea
                                            id="billingAddress"
                                            value={billingInfo.billingAddress}
                                            onChange={(e) => setBillingInfo({ ...billingInfo, billingAddress: e.target.value })}
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-content-end mt-3">
                                <Button label="Save Billing Info" icon="pi pi-check" onClick={saveBillingInfo} />
                            </div>
                        </Panel>
                        
                        <Panel header="Invoice History" className="mt-4">
                            <DataTable 
                                value={invoices}
                                paginator
                                rows={5}
                                className="invoice-table"
                            >
                                <Column field="id" header="Invoice Number" />
                                <Column field="date" header="Date" body={(rowData) => rowData.date.toLocaleDateString('en-IN')} />
                                <Column field="credits" header="Credits" />
                                <Column field="amount" header="Amount" body={(rowData) => `₹${rowData.amount.toLocaleString()}`} />
                                <Column field="status" header="Status" body={(rowData) => <Tag value={rowData.status.toUpperCase()} severity="success" />} />
                                <Column header="Action" body={invoiceActionTemplate} style={{ width: '80px' }} />
                            </DataTable>
                        </Panel>
                    </TabPanel>
                </TabView>
            </Card>
        </div>
    );
};

export default Settings;